import { customAuth } from "../standardAuthPatterns";
import {
  connectionString,
  type LdapDefaults,
  publicEnvVars,
} from "../ldap/shared";
import logo from "../microsoftGraph/logo.svg";
import { defineConnector } from "../types";

const DEFAULTS: LdapDefaults = { port: "636", tls: "implicit" };

export const activeDirectory = defineConnector({
  identifier: "activeDirectory",
  name: "Microsoft Active Directory",
  tagLine: "Bind to Active Directory over LDAP",
  logo,
  tags: ["identity", "it"],
  protocol: "ldap",
  connectionTest: {
    protocol: "ldap",
    connectionString: (config) => connectionString(config, DEFAULTS),
  },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("ldap"),
  steps: [
    {
      title: "Connect to Active Directory",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "Active Directory",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Domain controller host",
          required: true,
          placeholder: "dc01.corp.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "636",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          placeholder: "svc-account@corp.example.com",
          helperText:
            "A userPrincipalName (user@domain), DOMAIN\\user, or full bind DN.",
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "select",
          label: "TLS",
          required: true,
          defaultValue: "implicit",
          target: { kind: "configuration", field: "tls" },
          options: [
            { value: "implicit", label: "LDAPS (implicit TLS)" },
            { value: "off", label: "Disabled (plain LDAP)" },
          ],
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Upload your domain's CA certificate so the LDAPS server cert validates.",
          target: { kind: "configuration", field: "tlsCaCertificate" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "tls" },
              notValue: "off",
            },
          ],
        },
      ],
    },
  ],
  publicEnvVars: (config) => publicEnvVars(config, DEFAULTS),
  usageHint: () =>
    "Read LDAP_HOST, LDAP_PORT, LDAP_BIND_DN, and LDAP_PASSWORD from the environment to connect. The bind happens at the proxy, so LDAP_PASSWORD is a placeholder — send it as-is. Active Directory usually requires LDAPS (TLS) for simple bind.",
});
